/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1791331200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 07 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "open-ai",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to OpenAI",
          "depth": 0,
          "id": "connect-to-openai"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Syncing segments",
          "depth": 1,
          "id": "syncing-segments"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to OpenAI conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.openai.com/ads/conversions-api",
                children: "Events docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Segments"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to ChatGPT (OpenAI) custom audiences"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.openai.com/ads/custom-audiences",
                children: "Segments docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-openai",
        children: "Connect to OpenAI"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "OpenAI"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "OpenAI"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Enter the following fields into Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Pixel ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "API key"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Ads API key"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Ad account ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your OpenAI destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the OpenAI destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to OpenAI conversion events."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to events in OpenAI on any column in your model and any field in OpenAI. Ensure the data types of the model column and OpenAI field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties via field mapping."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-segments",
        children: "Syncing segments"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to ChatGPT (OpenAI) custom audiences."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["You can match rows from your model to segments in ChatGPT Ads (OpenAI) on any column in your model and any field in ChatGPT Ads (OpenAI). Ensure the data types of the model column and ChatGPT Ads (OpenAI) field you select match.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync segment fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default segment fields.\nEnsure your model's columns have the same data types as the fields you want to sync to."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
